import { BrandTheme } from '../types/theme';
import { TenantInfo } from '../services/hotel.service';
import { DEFAULT_THEME } from './defaultTheme';

/**
 * Converts API brandDetails to BrandTheme
 */
export const convertBrandDetailsToTheme = (tenantInfo: TenantInfo): BrandTheme => {
  const brandDetails = tenantInfo.brandDetails;
  

  // Create theme by merging API brandDetails with default theme
  // This ensures aiResponseSystem.contentTypes always come from defaults
  const theme: BrandTheme = {
    ...DEFAULT_THEME,
    // Use API brand details for brand colors and UI components
    brand: brandDetails.brand as any, // Type assertion for partial brand object from API
    userInterface: {
      ...brandDetails.userInterface,
      sendButton: {
        ...brandDetails.userInterface.sendButton,
        hover: {
          ...brandDetails.userInterface.sendButton.hover,
          // Ensure hover text is set from default
          text: DEFAULT_THEME.userInterface.sendButton.hover.text
        },
        disabled: {
          ...brandDetails.userInterface.sendButton.disabled,
          // Ensure disabled text is set
          text: brandDetails.userInterface.sendButton.disabled.text || '#ffffff'
        }
      }
    },
    // Use API aiResponseSystem structure with API contentTypes
    aiResponseSystem: {
      responseCard: {
        ...brandDetails.aiResponseSystem.responseCard,
        // Ensure padding is set
        padding: brandDetails.aiResponseSystem.responseCard.padding || {
          default: '24px',
          large: '32px'
        }
      },
      avatar: brandDetails.aiResponseSystem.avatar,
      // Use API contentTypes but merge with defaults for missing properties
      contentTypes: (() => {
        const apiSuggestionCard = (brandDetails.aiResponseSystem?.contentTypes as any)?.suggestionCard;

        // Start with defaults
        const baseContentTypes = { ...DEFAULT_THEME.aiResponseSystem.contentTypes };

        // Merge API contentTypes except suggestionCard
        const apiContentTypes = { ...brandDetails.aiResponseSystem.contentTypes } as any;
        delete apiContentTypes.suggestionCard; // Remove suggestionCard to handle separately

        const mergedContentTypes = {
          ...baseContentTypes,
          ...apiContentTypes
        } as any;

        // Handle suggestionCard specifically
        if (apiSuggestionCard) {
          mergedContentTypes.suggestionCard = {
            // Start with defaults
            ...(DEFAULT_THEME.aiResponseSystem.contentTypes as any).suggestionCard,
            // Override with API values
            ...apiSuggestionCard,
            // Ensure nested objects exist with all required properties
            accentBar: {
              ...((DEFAULT_THEME.aiResponseSystem.contentTypes as any).suggestionCard as any)?.accentBar,
              ...(apiSuggestionCard as any)?.accentBar
            },
            hover: {
              ...((DEFAULT_THEME.aiResponseSystem.contentTypes as any).suggestionCard as any)?.hover,
              ...(apiSuggestionCard as any)?.hover
            },
            focus: {
              ...((DEFAULT_THEME.aiResponseSystem.contentTypes as any).suggestionCard as any)?.focus,
              ...(apiSuggestionCard as any)?.focus
            },
            iconRing: {
              ...((DEFAULT_THEME.aiResponseSystem.contentTypes as any).suggestionCard as any)?.iconRing,
              ...(apiSuggestionCard as any)?.iconRing
            },
            actionLabel: {
              ...((DEFAULT_THEME.aiResponseSystem.contentTypes as any).suggestionCard as any)?.actionLabel,
              ...(apiSuggestionCard as any)?.actionLabel
            },
            hintLabel: {
              ...((DEFAULT_THEME.aiResponseSystem.contentTypes as any).suggestionCard as any)?.hintLabel,
              ...(apiSuggestionCard as any)?.hintLabel
            },
            sectionLabel: {
              ...((DEFAULT_THEME.aiResponseSystem.contentTypes as any).suggestionCard as any)?.sectionLabel,
              ...(apiSuggestionCard as any)?.sectionLabel
            }
          };
        }

        return mergedContentTypes as any;
      })()
    },
    // Use API aiAssistant info
    aiAssistant: brandDetails.aiAssistant,
    // Always use default accessibility settings
    accessibility: DEFAULT_THEME.accessibility,
    // Use API transactionalUI if available, otherwise fallback to default
    transactionalUI: brandDetails.transactionalUI || DEFAULT_THEME.transactionalUI
  };

  return theme;
};

/**
 * Creates a theme from tenant info with fallback to default theme
 */
export const createThemeFromTenantInfo = (tenantInfo: TenantInfo | null): BrandTheme => {
  if (!tenantInfo) {
    return DEFAULT_THEME;
  }

  try {
    return convertBrandDetailsToTheme(tenantInfo);
  } catch (error) {
    return DEFAULT_THEME;
  }
};